import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  User,
  Shield,
  FileText,
  LogOut,
  ChevronDown,
  Gift,
  HelpCircle,
  Phone,
  Info,
  Users,
  UserCheck,
  Scale,
  Heart,
  Activity,
  Ticket,
  Calendar,
  Briefcase,
  ArrowLeft,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import { clearAuth } from "../../store/slices/authSlice";
import Images from "../../utils/Images";
import DropdownMenu from "./DropdownMenu";
import MegaMenu from "./MegaMenu";

// Base nav links
const baseNavLinks = [
  { name: "Home", path: "/" },
  {
    name: "Support",
    type: "dropdown",
    items: [
      {
        title: "Help Center",
        description: "Find answers to your questions",
        href: "/help-center",
        icon: HelpCircle,
      },
      {
        title: "Safety Information",
        description: "Travel safety guidelines",
        href: "/safety-information",
        icon: Shield,
      },
      {
        title: "Contact Us",
        description: "Get in touch with support",
        href: "/contact-us",
        icon: Phone,
      },
    ],
  },
];

// Standard nav links (with Company and Partners)
const standardNavLinks = [
  ...baseNavLinks,
  {
    name: "Company",
    type: "dropdown",
    items: [
      {
        title: "About Us",
        description: "Learn about TripSecure",
        href: "/about-us",
        icon: Info,
      },
      {
        title: "Privacy Policy",
        description: "How we protect your data",
        href: "/privacy-policy",
        icon: FileText,
      },
      {
        title: "Terms of Service",
        description: "Our terms and conditions",
        href: "/terms-of-service",
        icon: Scale,
      },
    ],
  },
  {
    name: "Partners",
    type: "dropdown",
    items: [
      {
        title: "Become a Provider",
        description: "Join our partner network",
        href: "/become-a-provider",
        icon: Users,
      },
      {
        title: "Become an Agent",
        description: "Start earning as an agent",
        href: "/become-an-agent",
        icon: UserCheck,
      },
    ],
  },
];

// Media events base nav links (with Media & Events specific Support)
const mediaEventsBaseNavLinks = [
  { name: "Home", path: "/media-events" },
  {
    name: "Support",
    type: "dropdown",
    items: [
      {
        title: "Media & Events Overview",
        description: "Learn about TripSecure Media & Events",
        href: "/media-events-overview",
        icon: Info,
      },
      {
        title: "Help Center – Media & Events",
        description: "Find answers to your questions",
        href: "/help-center-media-events",
        icon: HelpCircle,
      },
      {
        title: "Contact Us – Media & Events",
        description: "Get in touch with support",
        href: "/contact-us-media-events",
        icon: Phone,
      },
    ],
  },
  {
    name: "Company",
    type: "dropdown",
    items: [
      {
        title: "About Us",
        description: "Learn about TripSecure",
        href: "/about-us",
        icon: Info,
      },
      {
        title: "Privacy Policy",
        description: "How we protect your data",
        href: "/privacy-policy",
        icon: FileText,
      },
      {
        title: "Terms of Service",
        description: "Our terms and conditions",
        href: "/terms-of-service",
        icon: Scale,
      },
    ],
  },
  {
    name: "Partner",
    type: "dropdown",
    items: [
      {
        title: "Become a partner",
        description: "Partner information coming soon",
        href: null,
        icon: Briefcase,
        disabled: true,
      },
      {
        title: "Become an agent",
        description: "Start earning as an agent",
        href: "/become-an-agent",
        icon: UserCheck,
      },
    ],
  },
];

// Media events nav links (with My-Activity)
const mediaEventsNavLinks = [
  ...mediaEventsBaseNavLinks,
  {
    name: "My-Activity",
    type: "dropdown",
    items: [
      {
        title: "Conference Registrations",
        description: "Manage your conference bookings",
        href: "/conference-registrations",
        icon: Calendar,
      },
      {
        title: "My Event Bookings",
        description: "Manage your event tickets",
        href: "/my-event-registrations",
        icon: Ticket,
      },
      {
        title: "Trade Mission Registrations",
        description: "Track your event applications",
        href: "/my-registrations",
        icon: Briefcase,
      },
    ],
  },
];

// Mobile Dropdown Menu Component
const MobileDropdownMenu = ({ title, items, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center px-4 py-3 w-full text-left text-gray-700 rounded-lg transition-colors hover:bg-gray-50 hover:text-green-600"
      >
        <div className="flex gap-3 items-center">
          {Icon && <Icon className="w-4 h-4" />}
          <span className="font-medium">{title}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-4 space-y-1"
          >
            {items.map((item, index) => {
              const isDisabled = item.disabled || !item.href;
              
              return (
                <div key={index}>
                  {isDisabled ? (
                    <div className="flex gap-3 items-center px-4 py-2 text-sm text-gray-400 rounded-lg cursor-not-allowed opacity-60">
                      {item.icon && <item.icon className="w-4 h-4" />}
                      <div>
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-400">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-600 rounded-lg transition-colors hover:bg-green-50 hover:text-green-600"
                    >
                      {item.icon && <item.icon className="w-4 h-4" />}
                      <div>
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-600 rounded-lg transition-colors hover:bg-green-50 hover:text-green-600"
                    >
                      {item.icon && <item.icon className="w-4 h-4" />}
                      <div>
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </Link>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function AuthNavbar({ user }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const location = useLocation();

  const isDirectMediaEventsPage =
    location.pathname.startsWith("/media-events") ||
    location.pathname.startsWith("/conference-trade-mission") ||
    location.pathname.startsWith("/events") ||
    location.pathname.startsWith("/event-details") ||
    location.pathname.startsWith("/my-event-registrations") ||
    location.pathname.startsWith("/my-registrations") ||
    location.pathname.startsWith("/conference-registration") ||
    location.pathname.startsWith("/conference-registrations") ||
    location.pathname.startsWith("/media-events-overview") ||
    location.pathname.startsWith("/help-center-media-events") ||
    location.pathname.startsWith("/contact-us-media-events");

  // Check if we're on a Company page and came from Media & Events
  const isCompanyPage =
    location.pathname === "/about-us" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-of-service";
  
  // Check if we're on Settings/Profile page and came from Media & Events
  const isSettingsPage = location.pathname === "/settings";
  
  const isFromMediaEvents =
    sessionStorage.getItem("isMediaEventsContext") === "true";

  const isMediaEventsPage =
    isDirectMediaEventsPage || 
    (isCompanyPage && isFromMediaEvents) ||
    (isSettingsPage && isFromMediaEvents);

  // Set Media & Events context when on Media & Events pages
  useEffect(() => {
    if (isDirectMediaEventsPage) {
      sessionStorage.setItem("isMediaEventsContext", "true");
    } else if ((isCompanyPage || isSettingsPage) && !isFromMediaEvents) {
      // Clear context if on Company page or Settings but didn't come from Media & Events
      sessionStorage.removeItem("isMediaEventsContext");
    } else if (
      !isCompanyPage &&
      !isSettingsPage &&
      !isDirectMediaEventsPage &&
      location.pathname === "/"
    ) {
      sessionStorage.removeItem("isMediaEventsContext");
    }
  }, [
    isDirectMediaEventsPage,
    isCompanyPage,
    isSettingsPage,
    isFromMediaEvents,
    location.pathname,
  ]);

  const navLinks = isMediaEventsPage ? mediaEventsNavLinks : standardNavLinks;

  const currentUser = useMemo(() => {
    return (
      user || {
        name: "Alex Carter",
        email: "alex@example.com",
        avatarUrl: "",
      }
    );
  }, [user]);

  const userInitials = useMemo(() => {
    const parts = (
      currentUser ? `${currentUser?.first_name} ${currentUser?.last_name}` : ""
    )
      .split(" ")
      .filter(Boolean);
    if (!parts.length) return "U";
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
  }, [currentUser]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollY / windowHeight) * 100;

      setIsScrolled(scrollY > 20);
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isDropdownOpen]);

  const isActiveLink = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    if (path === "/media-events") {
      // For media-events home, match exactly or paths that start with /media-events/
      return (
        location.pathname === "/media-events" ||
        location.pathname.startsWith("/media-events/")
      );
    }
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      document.body.style.overflow = "unset";
    } else {
      document.body.style.overflow = "hidden";
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = "unset";
  };

  const handleLogout = () => {
    dispatch(clearAuth());
    navigate("/");
  };

  return (
    <>
      {/* Desktop Navbar */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "border-b shadow-lg backdrop-blur-md bg-white/95 border-gray-200/50"
            : "backdrop-blur-md bg-black/95"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          boxShadow: isScrolled
            ? "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Scroll Progress Bar */}
        <motion.div
          className="absolute top-0 left-0 z-10 h-1 bg-gradient-to-r from-green-500 to-emerald-500"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          transition={{ duration: 0.1 }}
        />
        <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={Images.logo_navbar}
                  alt="TripSecure"
                  className={`h-7 transition-all duration-300 ${
                    isScrolled
                      ? "filter brightness-0"
                      : "filter brightness-0 invert"
                  }`}
                />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex lg:items-center lg:space-x-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="relative group"
                >
                  {link.type === "dropdown" ? (
                    <DropdownMenu
                      title={link.name}
                      items={link.items}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : link.type === "mega" ? (
                    <MegaMenu
                      title={link.name}
                      sections={link.sections}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : (
                    <Link
                      to={link.path}
                      className={`relative px-3 py-2 text-sm font-medium transition-all duration-300 ${
                        isScrolled
                          ? isActiveLink(link.path)
                            ? "text-green-600"
                            : "text-gray-700 hover:text-green-600"
                          : isActiveLink(link.path)
                          ? "text-green-400"
                          : "text-white hover:text-green-400"
                      }`}
                    >
                      {link.name}
                      {/* Active indicator */}
                      {isActiveLink(link.path) && (
                        <motion.div
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"
                          layoutId="activeTabAuth"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                          }}
                        />
                      )}
                      {/* Hover underline */}
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-current"
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.3 }}
                        style={{ transformOrigin: "left" }}
                      />
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>

            {/* User Menu (replaces CTA) */}
            <div className="hidden relative lg:flex lg:items-center lg:space-x-4">
              <motion.button
                ref={profileButtonRef}
                onClick={() => setIsDropdownOpen((v) => !v)}
                className={`group flex items-center gap-3 px-2 py-1.5 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? "hover:bg-gray-100"
                    : "hover:bg-white/10 text-white"
                }`}
                whileHover={{ y: -1, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="inline-flex relative">
                  {currentUser?.avatarUrl ? (
                    <img
                      src={currentUser.avatarUrl}
                      alt={currentUser.name}
                      className="object-cover w-9 h-9 rounded-full ring-2 ring-emerald-500/40"
                    />
                  ) : (
                    <div className="grid place-items-center w-9 h-9 text-sm font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-full ring-2 ring-emerald-500/40">
                      {userInitials}
                    </div>
                  )}
                  {/* online dot */}
                  <span className="absolute -right-0.5 -bottom-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
                </span>
                <div className="hidden text-left xl:block">
                  <p
                    className={`text-sm font-semibold leading-5 ${
                      isScrolled ? "text-gray-800" : "text-white"
                    }`}
                  >
                    {currentUser
                      ? `${currentUser?.first_name} ${currentUser?.last_name}`
                      : "User"}
                  </p>
                  <p className="text-xs text-gray-500 truncate max-w-[160px]">
                    {currentUser?.email || ""}
                  </p>
                </div>
                <ChevronDown
                  className={`transition-transform duration-300 ${
                    isDropdownOpen ? "rotate-180" : ""
                  } ${isScrolled ? "text-gray-600" : "text-white"}`}
                  size={18}
                />
              </motion.button>

              {/* Dropdown */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    ref={dropdownRef}
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    className="absolute right-0 top-[calc(100%+10px)] w-80 bg-white backdrop-blur-xl border border-emerald-100 shadow-2xl rounded-2xl overflow-hidden"
                  >
                    {/* Top Accent Bar */}
                    <div className="w-full h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />

                    {/* Profile summary */}
                    <div className="flex gap-3 items-center p-4">
                      <div className="relative">
                        {currentUser?.avatarUrl ? (
                          <img
                            src={currentUser.avatarUrl}
                            alt={currentUser.name}
                            className="object-cover w-12 h-12 rounded-full ring-2 ring-emerald-500/40"
                          />
                        ) : (
                          <div className="grid place-items-center w-12 h-12 text-base font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-full ring-2 ring-emerald-500/40">
                            {userInitials}
                          </div>
                        )}
                        <span className="absolute -right-0.5 -bottom-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold leading-5 text-gray-900 truncate">
                          {currentUser
                            ? `${currentUser?.first_name} ${currentUser?.last_name}`
                            : "User"}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {currentUser?.email}
                        </p>
                      </div>
                    </div>

                    {/* Quick links */}
                    {!isMediaEventsPage && (
                      <div className="px-2 pb-2">
                        <div className="grid grid-cols-3 gap-2">
                          <Link
                            to="/my-policies"
                            className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 transition-colors group bg-emerald-50/60 hover:bg-emerald-50"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                              <Shield size={20} />
                            </span>
                            <span className="mt-1.5 text-xs font-medium text-emerald-700">
                              Policies
                            </span>
                          </Link>
                          <Link
                            to="/claim/list"
                            className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 transition-colors group bg-emerald-50/60 hover:bg-emerald-50"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                              <FileText size={20} />
                            </span>
                            <span className="mt-1.5 text-xs font-medium text-emerald-700">
                              Claims
                            </span>
                          </Link>
                          <Link
                            to="/settings"
                            className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 transition-colors group bg-emerald-50/60 hover:bg-emerald-50"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                              <User size={20} />
                            </span>
                            <span className="mt-1.5 text-xs font-medium text-emerald-700">
                              Profile
                            </span>
                          </Link>
                        </div>
                      </div>
                    )}


                    <div className="px-3">
                      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-100 to-transparent" />
                    </div>

                    {/* Full list */}
                    <div className="p-1">
                      {!isMediaEventsPage && (
                        <>
                          <Link
                            to="/my-policies"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-emerald-700 bg-emerald-100 rounded-lg">
                              <Shield size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">
                                My Policies
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                View and manage your insurance policies
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/claim/list"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-emerald-700 bg-emerald-100 rounded-lg">
                              <FileText size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">
                                Claims
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                File or track your claims
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/my-quote-requests"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-amber-700 bg-amber-100 rounded-lg">
                              <FileText size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">
                                My Quote Requests
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                View and pay for insurance quotes
                              </p>
                            </div>
                          </Link>

                          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl opacity-60 cursor-not-allowed relative">
                            <span className="flex justify-center items-center w-9 h-9 text-gray-400 bg-gray-100 rounded-lg">
                              <Gift size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-500">
                                Rewards
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                Coming Soon
                              </p>
                            </div>
                            <span className="text-xs font-medium text-gray-400">
                              Coming Soon
                            </span>
                          </div>
                        </>
                      )}

                      {isMediaEventsPage && (
                        <>
                          <Link
                            to="/conference-registrations"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-orange-700 bg-orange-100 rounded-lg">
                              <Calendar size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">
                                Conference Registrations
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                Manage your conference bookings
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/my-event-registrations"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-emerald-700 bg-emerald-100 rounded-lg">
                              <Ticket size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">
                                My Event Bookings
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                Manage your event tickets
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/my-registrations"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-blue-700 bg-blue-100 rounded-lg">
                              <Briefcase size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">
                                Trade Mission Registrations
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                Track your event applications
                              </p>
                            </div>
                          </Link>
                        </>
                      )}

                      <Link
                        to="/settings"
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <span className="flex justify-center items-center w-9 h-9 text-emerald-700 bg-emerald-100 rounded-lg">
                          <User size={18} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">
                            Profile
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            Personal information and settings
                          </p>
                        </div>
                      </Link>
                    </div>

                    <div className="px-3 pb-3">
                      <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-100 to-transparent" />
                    </div>

                    {/* Logout */}
                    <div className="px-2 pb-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-gradient-to-r from-red-50 to-white hover:from-red-100 hover:to-white text-red-600 border border-red-100 transition-colors"
                      >
                        <span className="flex gap-2 items-center">
                          <span className="flex justify-center items-center w-8 h-8 text-red-600 bg-red-100 rounded-lg">
                            <LogOut size={18} />
                          </span>
                          <span className="text-sm font-semibold">Logout</span>
                        </span>
                        <span className="text-xs text-red-500/80">
                          Securely sign out
                        </span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-md transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-green-600 hover:bg-gray-100"
                    : "text-white hover:text-green-400 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu size={24} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-40 backdrop-blur-sm bg-black/50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeMobileMenu}
            />

            {/* Mobile Menu */}
            <motion.div
              className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex overflow-hidden flex-col h-full">
                {/* Mobile Menu Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                  <img
                    src={Images.logo_navbar}
                    alt="TripSecure"
                    className="h-8 filter brightness-0"
                  />
                  <motion.button
                    onClick={closeMobileMenu}
                    className="p-2 text-gray-500 rounded-md hover:text-gray-700 hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={24} />
                  </motion.button>
                </div>

                {/* User Card */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex gap-3 items-center">
                    {currentUser?.avatarUrl ? (
                      <img
                        src={currentUser.avatarUrl}
                        alt={currentUser.name}
                        className="object-cover w-12 h-12 rounded-full ring-2 ring-emerald-500/40"
                      />
                    ) : (
                      <div className="grid place-items-center w-12 h-12 text-base font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-full ring-2 ring-emerald-500/40">
                        {userInitials}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold leading-5 text-gray-900 truncate">
                        {currentUser
                          ? `${currentUser?.first_name} ${currentUser?.last_name}`
                          : "User"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {currentUser?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mobile Navigation Links */}
                <nav className="overflow-y-auto flex-1 px-6 py-6 space-y-2">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + index * 0.08, duration: 0.3 }}
                    >
                      {link.type === "dropdown" || link.type === "mega" ? (
                        <MobileDropdownMenu
                          title={link.name}
                          items={link.items || link.sections}
                          icon={link.icon}
                        />
                      ) : (
                        <Link
                          to={link.path}
                          onClick={closeMobileMenu}
                          className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                            isActiveLink(link.path)
                              ? "bg-green-50 text-green-700 border-l-4 border-green-500"
                              : "text-gray-700 hover:bg-gray-50 hover:text-green-600"
                          }`}
                        >
                          {link.name}
                        </Link>
                      )}
                    </motion.div>
                  ))}

                  {/* Mobile actions */}
                  {!isMediaEventsPage && (
                    <>
                      <div className="grid grid-cols-3 gap-3 pt-2">
                        <Link
                          to="/my-policies"
                          onClick={closeMobileMenu}
                          className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
                        >
                          <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                            <Shield size={20} />
                          </span>
                          <span className="mt-1.5 text-xs font-medium text-emerald-700">
                            Policies
                          </span>
                        </Link>

                        <Link
                          to="/claim/list"
                          onClick={closeMobileMenu}
                          className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
                        >
                          <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                            <FileText size={20} />
                          </span>
                          <span className="mt-1.5 text-xs font-medium text-emerald-700">
                            Claims
                          </span>
                        </Link>

                        <Link
                          to="/settings"
                          onClick={closeMobileMenu}
                          className="flex flex-col justify-center items-center p-3 rounded-xl border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50"
                        >
                          <span className="flex justify-center items-center w-10 h-10 text-emerald-600 bg-white rounded-xl shadow-sm">
                            <User size={20} />
                          </span>
                          <span className="mt-1.5 text-xs font-medium text-emerald-700">
                            Profile
                          </span>
                        </Link>
                      </div>
                    </>
                  )}

                  {isMediaEventsPage && (
                    <>
                      <div className="pt-4 mt-4 border-t border-gray-200">
                        <p className="px-2 mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                          My Registrations
                        </p>
                        <div className="space-y-2">
                          <Link
                            to="/conference-registrations"
                            onClick={closeMobileMenu}
                            className="flex gap-3 items-center px-4 py-3 text-sm text-gray-700 bg-white rounded-xl border border-gray-200 transition-colors hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-orange-700 bg-orange-100 rounded-lg">
                              <Calendar size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800">
                                Conference Registrations
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                Manage conference bookings
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/my-event-registrations"
                            onClick={closeMobileMenu}
                            className="flex gap-3 items-center px-4 py-3 text-sm text-gray-700 bg-white rounded-xl border border-gray-200 transition-colors hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-emerald-700 bg-emerald-100 rounded-lg">
                              <Ticket size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800">
                                My Event Bookings
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                Manage event tickets
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/my-registrations"
                            onClick={closeMobileMenu}
                            className="flex gap-3 items-center px-4 py-3 text-sm text-gray-700 bg-white rounded-xl border border-gray-200 transition-colors hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-blue-700 bg-blue-100 rounded-lg">
                              <Briefcase size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800">
                                My Registrations
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                Track event applications
                              </p>
                            </div>
                          </Link>

                          <Link
                            to="/my-quote-requests"
                            onClick={closeMobileMenu}
                            className="flex gap-3 items-center px-4 py-3 text-sm text-gray-700 bg-white rounded-xl border border-gray-200 transition-colors hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700"
                          >
                            <span className="flex justify-center items-center w-9 h-9 text-amber-700 bg-amber-100 rounded-lg">
                              <FileText size={18} />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800">
                                My Quote Requests
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                View and pay for quotes
                              </p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </nav>

                {/* Mobile Logout */}
                <div className="p-6 border-t border-gray-200">
                  <motion.button
                    className="flex gap-2 justify-center items-center px-6 py-3 w-full font-medium text-red-600 bg-red-50 rounded-lg border border-red-100 shadow-sm transition-all duration-300 hover:bg-red-100"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleLogout}
                  >
                    <LogOut size={18} /> Logout
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Tablet Navigation (Hidden on mobile and desktop) */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 md:block lg:hidden ${
          isScrolled
            ? "border-b shadow-lg backdrop-blur-md bg-white/95 border-gray-200/50"
            : "backdrop-blur-md bg-black/95"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          boxShadow: isScrolled
            ? "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Scroll Progress Bar */}
        <motion.div
          className="absolute top-0 left-0 z-10 h-1 bg-gradient-to-r from-green-500 to-emerald-500"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          transition={{ duration: 0.1 }}
        />
        <div className="px-4 mx-auto max-w-4xl sm:px-6">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={Images.logo_navbar}
                  alt="TripSecure"
                  className={`h-7 transition-all duration-300 ${
                    isScrolled
                      ? "filter brightness-0"
                      : "filter brightness-0 invert"
                  }`}
                />
              </Link>
            </div>

            {/* Tablet Navigation Links */}
            <div className="hidden md:flex md:items-center md:space-x-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {link.type === "dropdown" ? (
                    <DropdownMenu
                      title={link.name}
                      items={link.items}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : link.type === "mega" ? (
                    <MegaMenu
                      title={link.name}
                      sections={link.sections}
                      icon={link.icon}
                      isScrolled={isScrolled}
                    />
                  ) : (
                    <Link
                      to={link.path}
                      className={`relative px-2 py-2 text-sm font-medium transition-all duration-300 ${
                        isScrolled
                          ? isActiveLink(link.path)
                            ? "text-green-600"
                            : "text-gray-700 hover:text-green-600"
                          : isActiveLink(link.path)
                          ? "text-green-400"
                          : "text-white hover:text-green-400"
                      }`}
                    >
                      {link.name}
                      {isActiveLink(link.path) && (
                        <motion.div
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"
                          layoutId="activeTabTabletAuth"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                          }}
                        />
                      )}
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Tablet profile button */}
            <div className="hidden md:flex md:items-center">
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-green-600 hover:bg-gray-100"
                    : "text-white hover:text-green-400 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <User size={22} />
              </motion.button>
            </div>

            {/* Mobile menu button for smaller tablets */}
            <div className="md:hidden">
              <motion.button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-md transition-all duration-300 ${
                  isScrolled
                    ? "text-gray-700 hover:text-green-600 hover:bg-gray-100"
                    : "text-white hover:text-green-400 hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu size={24} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Floating Action Button for Mobile */}
      <motion.div
        className="fixed right-6 bottom-6 z-40 lg:hidden"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 400, damping: 10 }}
      >
        <motion.button
          className="flex justify-center items-center w-14 h-14 text-white bg-green-600 rounded-full shadow-2xl"
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleMobileMenu}
        >
          <Menu size={24} />
        </motion.button>
      </motion.div>
    </>
  );
}
